import { Pencil } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  BackofficePageModeActionMenu,
  CustomizableDetailTabs,
  DetailPanel,
  DetailPanelGrid,
  PageHeader,
  panelSpanFromLayout,
  type DetailPanelGridItem,
  type TableActionItem,
  useBackofficePageMode,
  useCustomizableDetailTabs,
} from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import {
  archiveBackofficeLink,
  fetchBackofficeLinkAccesses,
  fetchBackofficeLink,
  restoreBackofficeLink,
  searchBackofficeStudies,
  updateBackofficeLink,
  updateBackofficeLinkQrCode,
} from './api';
import {
  LinkCurrentTargetTile,
  LinkDestinationHistoryTile,
  LinkDetailsTile,
  LinkEditorModal,
  LinkQrCodePanel,
  LinkQrCodeSettingsModal,
  LinkTrackingUsageTile,
} from './components';
import { formatLinkStateLabel, formatResolutionStateLabel } from './format';
import type { BackofficeLink, BackofficeLinkQrCodeSettings, BackofficeStudyOption } from './types';

type LinkTab = 'overview';

const LINK_BUILT_IN_TABS = [{ id: 'overview', label: 'Overview' }] satisfies Array<{
  id: LinkTab;
  label: string;
}>;

function StatusChip({ label, tone }: { label: string; tone: 'neutral' | 'success' }) {
  return (
    <span
      className={[
        'inline-flex rounded-full px-2.5 py-1 text-small font-medium',
        tone === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-700',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

function confirmArchiveLink(): boolean {
  return window.confirm('Archive this link? You can restore it later.');
}

export function BackofficeLinkDetailsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [link, setLink] = useState<BackofficeLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageCount, setUsageCount] = useState(0);
  const [editorMode, setEditorMode] = useState<'edit' | 'restore' | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [editorSubmitting, setEditorSubmitting] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);
  const [qrSubmitting, setQrSubmitting] = useState(false);
  const { pageMode, setPageMode } = useBackofficePageMode();

  useBackofficePageTitle(link ? `Link: ${link.slug}` : 'Link');

  const detailTabs = useCustomizableDetailTabs<LinkTab>({
    preferenceScope: 'details:links',
    builtInTabs: LINK_BUILT_IN_TABS,
    defaultTabId: 'overview',
    basePath: slug ? `/links/${slug}` : '/links',
    pageMode,
  });

  const loadLink = useCallback(
    async (signal?: AbortSignal) => {
      if (!slug) {
        setError('Missing link slug');
        setLink(null);
        setUsageCount(0);
        return;
      }

      const [linkResponse, accessesResponse] = await Promise.all([
        fetchBackofficeLink(slug, signal),
        fetchBackofficeLinkAccesses(slug, signal),
      ]);
      setLink(linkResponse.link);
      setUsageCount(accessesResponse.items.length);
    },
    [slug]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    loadLink(controller.signal)
      .catch((caught) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : 'Failed to load link');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [loadLink]);

  async function handleSearchStudies(query: string): Promise<BackofficeStudyOption[]> {
    const response = await searchBackofficeStudies(query);
    return response.studies;
  }

  async function handleArchive() {
    if (!link) return;

    setEditorSubmitting(true);
    setEditorError(null);
    try {
      await archiveBackofficeLink(link.slug);
      await loadLink();
      setEditorMode(null);
    } catch (caught) {
      setEditorError(caught instanceof Error ? caught.message : 'Failed to archive link');
    } finally {
      setEditorSubmitting(false);
    }
  }

  async function handleEditorSubmit(input: {
    slug?: string;
    target?: unknown;
    description?: string;
    qrCode?: BackofficeLinkQrCodeSettings;
  }) {
    if (!link) return;

    setEditorSubmitting(true);
    setEditorError(null);
    try {
      const response =
        editorMode === 'restore'
          ? await restoreBackofficeLink(link.slug, {
              ...(input.target ? { target: input.target as never } : {}),
              ...(input.description ? { description: input.description } : {}),
            })
          : await updateBackofficeLink(link.slug, {
              target: input.target as never,
              ...(input.description ? { description: input.description } : {}),
            });
      const nextLink =
        editorMode === 'edit' && input.qrCode
          ? (
              await updateBackofficeLinkQrCode(link.slug, {
                qrCode: input.qrCode,
              })
            ).link
          : response.link;
      setLink(nextLink);
      setEditorMode(null);
    } catch (caught) {
      setEditorError(caught instanceof Error ? caught.message : 'Failed to save link');
    } finally {
      setEditorSubmitting(false);
    }
  }

  async function handleQrSubmit(qrCode: BackofficeLinkQrCodeSettings) {
    if (!link) return;

    setQrSubmitting(true);
    setQrError(null);
    try {
      const response = await updateBackofficeLinkQrCode(link.slug, { qrCode });
      setLink(response.link);
      setQrModalOpen(false);
    } catch (caught) {
      setQrError(caught instanceof Error ? caught.message : 'Failed to update QR code');
    } finally {
      setQrSubmitting(false);
    }
  }

  const headerActions = useMemo<TableActionItem[]>(
    () =>
      link
        ? [
            {
              id: 'edit-link',
              label: link.status === 'archived' ? 'Restore link' : 'Edit link',
              icon: Pencil,
              placement: 'fixed-inline',
              onSelect: () => {
                setEditorError(null);
                setEditorMode(link.status === 'archived' ? 'restore' : 'edit');
              },
            },
            ...(link.status === 'active'
              ? [
                  {
                    id: 'archive-link',
                    label: 'Archive link',
                    onSelect: () => {
                      if (confirmArchiveLink()) {
                        void handleArchive();
                      }
                    },
                  } satisfies TableActionItem,
                ]
              : []),
          ]
        : [],
    [link]
  );

  const detailItems = useMemo<DetailPanelGridItem[]>(() => {
    if (!link) {
      return [];
    }

    return [
      {
        id: 'link-details',
        title: 'Link details',
        description: 'Slug, short link, status, and timestamps.',
        layout: { x: 0, y: 0, w: 2, h: 3, minW: 2, minH: 2, maxW: 3, maxH: 4, isResizable: true },
        render: (layout) => (
          <LinkDetailsTile
            link={link}
            panelId="link-details"
            currentSpan={panelSpanFromLayout(layout, '2x3')}
          />
        ),
      },
      {
        id: 'current-target',
        title: 'Current target',
        description: 'Current destination and resolution details.',
        layout: { x: 2, y: 0, w: 2, h: 3, minW: 2, minH: 2, maxW: 3, maxH: 4, isResizable: true },
        render: (layout) => (
          <LinkCurrentTargetTile
            link={link}
            panelId="current-target"
            currentSpan={panelSpanFromLayout(layout, '2x3')}
          />
        ),
      },
      {
        id: 'tracking-usage',
        title: 'Usage',
        description: 'Total tracked visits for this short link.',
        layout: { x: 4, y: 0, w: 1, h: 1, minW: 1, minH: 1, maxW: 2, maxH: 2, isResizable: true },
        render: (layout) => (
          <LinkTrackingUsageTile
            usageCount={usageCount}
            panelId="tracking-usage"
            currentSpan={panelSpanFromLayout(layout, '1x1')}
          />
        ),
      },
      {
        id: 'qr-code',
        title: 'QR code',
        description: 'Preview, download, and adjust the branded QR code.',
        layout: { x: 4, y: 1, w: 2, h: 2, minW: 2, minH: 2, maxW: 3, maxH: 4, isResizable: true },
        render: (layout) => (
          <DetailPanel
            title="QR code"
            span="2x2"
            panelId="qr-code"
            currentSpan={panelSpanFromLayout(layout, '2x2')}
          >
            <LinkQrCodePanel
              slug={link.slug}
              shortUrl={link.shortUrl}
              qrCode={link.qrCode}
              className="border-transparent bg-transparent p-0"
              headerActions={
                <button
                  type="button"
                  onClick={() => {
                    setQrError(null);
                    setQrModalOpen(true);
                  }}
                  className="focus-ring rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-small font-medium text-neutral-700 hover:bg-backoffice-50"
                >
                  Edit
                </button>
              }
            />
          </DetailPanel>
        ),
      },
      {
        id: 'destination-history',
        title: 'Destination history',
        description: 'Current and previous destinations for this reusable link.',
        layout: { x: 0, y: 3, w: 6, h: 3, minW: 3, minH: 2, maxW: 6, maxH: 5, isResizable: true },
        render: (layout) => (
          <LinkDestinationHistoryTile
            link={link}
            panelId="destination-history"
            currentSpan={panelSpanFromLayout(layout, '4x3')}
          />
        ),
      },
    ];
  }, [link, usageCount]);

  return (
    <main className="min-h-[320px] bg-neutral-50 p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader
          adornment={
            link ? (
              <>
                <StatusChip
                  label={formatLinkStateLabel(link.status)}
                  tone={link.status === 'active' ? 'success' : 'neutral'}
                />
                <StatusChip
                  label={formatResolutionStateLabel(link.currentResolution.state)}
                  tone={link.currentResolution.state === 'ready' ? 'success' : 'neutral'}
                />
              </>
            ) : null
          }
          actions={
            link ? (
              <BackofficePageModeActionMenu
                pageMode={pageMode}
                onPageModeChange={setPageMode}
                label="Link actions"
                viewActions={headerActions}
              />
            ) : null
          }
          showShareButton={pageMode !== 'edit'}
        >
          {link?.slug ?? 'Link'}
        </PageHeader>

        <CustomizableDetailTabs tabs={detailTabs} />

        {loading ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-neutral-500 shadow-sm">
            Loading link
          </div>
        ) : error ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-red-700 shadow-sm">
            {error}
          </div>
        ) : link ? (
          <DetailPanelGrid
            resetKey={link.slug}
            preferenceScope={`details:link:${detailTabs.activeTabId}`}
            layoutMode={pageMode}
            defaultVisibleItemIds={[
              'link-details',
              'current-target',
              'tracking-usage',
              'qr-code',
              'destination-history',
            ]}
            defaultTileSettings={{
              'link-details': {
                hiddenSettingIds: ['slug', 'resolution'],
              },
              'current-target': {
                hiddenSettingIds: ['target'],
              },
            }}
            tileCatalogLabel="Add link tile"
            items={detailItems}
          />
        ) : null}
      </div>

      {link && editorMode ? (
        <LinkEditorModal
          mode={editorMode}
          link={link}
          submitting={editorSubmitting}
          error={editorError}
          onClose={() => {
            if (!editorSubmitting) {
              setEditorMode(null);
            }
          }}
          onArchive={editorMode === 'edit' ? () => handleArchive() : undefined}
          onConfirmArchive={confirmArchiveLink}
          onCheckSlugAvailability={async () => true}
          onGenerateUniqueSlug={async () => link.slug}
          onSearchStudies={handleSearchStudies}
          onSubmit={handleEditorSubmit}
        />
      ) : null}

      {link ? (
        <LinkQrCodeSettingsModal
          open={qrModalOpen}
          qrCode={link.qrCode}
          submitting={qrSubmitting}
          error={qrError}
          onClose={() => {
            if (!qrSubmitting) {
              setQrModalOpen(false);
            }
          }}
          onSubmit={handleQrSubmit}
        />
      ) : null}
    </main>
  );
}
